import React from 'react';
import * as Sentry from '@sentry/react';
import ReactWebcam from 'react-webcam';
import {isFirefox, isMobile} from 'react-device-detect';
import clsx from 'clsx';
import {VideoConstraints, WebcamRefTypes} from './types';
import {compressFile, getCanvasBlob, getFileSizeMB, toBase64} from 'utils/common';
import {
  getMobileOrDesktopVideoConstraints,
  getReducedVideoConstraints,
  isMobileOrTablet,
  getPhotoCanvas,
} from './utils';
import {useErrorHandler} from './useErrorHandler';
import {
  Canvas,
  ScanArea,
  StyledWebcam,
  Wrapper,
  LoadingContainer,
  Spinner,
} from './styled';

const SCREENSHOT_FORMAT = 'image/jpeg';

export type WebcamProps = Partial<ReactWebcam> & {
  onDisabled?: (value: boolean) => void;
  onUserMedia?: () => void;
  imageSmoothing?: boolean;
  mirrored?: boolean;
  videoConstraints?: VideoConstraints;
  additionalConstraints?: VideoConstraints;
  hideScanArea?: boolean;
  compression?: boolean;
  roundedScanArea?: boolean;
  cropToScanArea?: boolean;
  isLoading?: boolean;
  placeholderImage?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  minScreenshotH?: number;
  minScreenshotW?: number;
  width?: number;
  height?: number;
  screenshotQuality?: number;
  screenshotMaxSize?: number;
  videoSourceDeviceId?: string;
};

const Webcam = React.memo(
  React.forwardRef<WebcamRefTypes, WebcamProps>(
    (
      {
        onUserMedia,
        onDisabled,
        imageSmoothing,
        videoConstraints,
        additionalConstraints,
        hideScanArea,
        roundedScanArea,
        cropToScanArea,
        className,
        mirrored,
        children,
        isLoading,
        placeholderImage,
        screenshotMaxSize,
        minScreenshotH,
        minScreenshotW,
        videoSourceDeviceId,
        screenshotQuality = 1,
        compression,
        ...props
      },
      ref,
    ) => {
      const [minScreenshotHeight, setMinScreenshotHeight] = React.useState<
        undefined | number
      >();
      const [minScreenshotWidth, setMinScreenshotWidth] = React.useState<
        undefined | number
      >();
      const [useReducedConstraints, setUseReducedConstraints] = React.useState(false);

      const constraints = React.useMemo(() => {
        if (videoConstraints) {
          return videoConstraints;
        }

        if (useReducedConstraints) {
          return getReducedVideoConstraints({
            deviceId: videoSourceDeviceId,
            additional: additionalConstraints,
          });
        }

        return getMobileOrDesktopVideoConstraints({
          deviceId: videoSourceDeviceId,
          additional: additionalConstraints,
        });
      }, [
        videoConstraints,
        additionalConstraints,
        videoSourceDeviceId,
        useReducedConstraints,
      ]);

      const isMirroredInCSS = mirrored === undefined && !isMobileOrTablet();
      const webcamRef = React.useRef<ReactWebcam>(null);
      const scanAreaRef = React.useRef<HTMLDivElement>(null);
      const canvas = React.useRef<HTMLCanvasElement>(null);
      const container = React.useRef<HTMLDivElement>(null);

      const [numberOfCameras, setNumberOfCameras] = React.useState<number>(0);

      const {handleUserMediaError, ErrorModal} = useErrorHandler({
        onError: () => onDisabled?.(true),
      });

      const handleUserMediaErrorWithFallback = React.useCallback(
        (error: string | DOMException | Error) => {
          // Check if it's an OverconstrainedError and we haven't tried basic constraints yet
          if (
            error &&
            error instanceof DOMException &&
            error.name === 'OverconstrainedError' &&
            !useReducedConstraints &&
            !videoConstraints
          ) {
            console.warn('Camera constraints failed, trying reduced constraints:', error);
            setUseReducedConstraints(true);
            return;
          }

          handleUserMediaError(error);
        },
        [handleUserMediaError, useReducedConstraints, videoConstraints],
      );

      const compressImage = async (canvas?: HTMLCanvasElement) => {
        if (!canvas) return;

        try {
          const blobImage = await getCanvasBlob(canvas);

          if (!blobImage) return;

          const compressedFile = await compressFile(blobImage, {
            convertSize: screenshotMaxSize,
          });

          console.log(
            'Before: ',
            getFileSizeMB(blobImage)?.toFixed(2),
            'MB',
            'After: ',
            getFileSizeMB(compressedFile)?.toFixed(2),
            'MB',
          );

          return await toBase64(compressedFile);
        } catch (err) {
          Sentry.captureException(err);
          throw err;
        }
      };

      const handleTakePhoto = () => {
        const takenPhotoCanvas = getPhotoCanvas({
          player: webcamRef.current?.video,
          container: container.current,
          canvas: canvas.current,
          scanArea: scanAreaRef.current,
          cropToScanArea,
        });

        if (!takenPhotoCanvas) {
          return;
        }

        if (compression) {
          return compressImage(takenPhotoCanvas);
        }

        return new Promise<string | undefined>(resolve => {
          resolve(takenPhotoCanvas.toDataURL(SCREENSHOT_FORMAT, screenshotQuality));
        });
      };

      React.useImperativeHandle(
        ref,
        () =>
          ({
            takePhoto: handleTakePhoto,
            numberOfCameras: numberOfCameras,
            ...webcamRef.current,
          } as WebcamRefTypes),
      );

      const handleUserMediaSuccess = React.useCallback(
        (stream: MediaStream) => {
          if (!isMobile) {
            const track = stream.getVideoTracks()[0];
            const aspectRatio = !isFirefox && track?.getCapabilities();

            if (aspectRatio && !isMobile) {
              setMinScreenshotHeight(aspectRatio?.height?.max);
              setMinScreenshotWidth(aspectRatio?.width?.max);
            }
          } else {
            setMinScreenshotWidth(minScreenshotW);
            setMinScreenshotHeight(minScreenshotH);
          }

          navigator.mediaDevices
            .enumerateDevices()
            .then(r => setNumberOfCameras(r.filter(i => i.kind === 'videoinput').length))
            .catch(error => {
              console.warn('Failed to enumerate devices:', error);
              setNumberOfCameras(1);
            });

          onUserMedia?.();
        },
        [minScreenshotH, minScreenshotW, onUserMedia],
      );

      return (
        <Wrapper
          ref={container}
          className={clsx(className, 'webcam')}
          $isMirroredInCSS={isMirroredInCSS}
        >
          {!hideScanArea && (
            <ScanArea
              className="webcam__scan-area"
              $rounded={roundedScanArea}
              ref={scanAreaRef}
            >
              <div />
              <div />
              <div />
              <div />
              {placeholderImage}
            </ScanArea>
          )}
          <Canvas ref={canvas} />
          {isLoading ? (
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          ) : (
            <StyledWebcam
              ref={webcamRef}
              mirrored={mirrored}
              width={props.width}
              height={props.height}
              videoConstraints={constraints}
              imageSmoothing={imageSmoothing}
              screenshotFormat={SCREENSHOT_FORMAT}
              onUserMedia={handleUserMediaSuccess}
              screenshotQuality={screenshotQuality}
              onUserMediaError={handleUserMediaErrorWithFallback}
              minScreenshotWidth={minScreenshotWidth}
              minScreenshotHeight={minScreenshotHeight}
              className="webcam__video"
              audio={false}
              disablePictureInPicture={false}
              forceScreenshotSourceSize={false}
            />
          )}
          {children}
          <ErrorModal />
        </Wrapper>
      );
    },
  ),
);

export {Webcam};
